//* Vue Router
import type { RouteLocationNormalizedGeneric } from "vue-router";
//* 组件库
import { $message } from "various-ui";
//* 请求库
import { FetchError } from "ofetch";
//* i18n配置
import { i18nOption } from "@/app/i18n";
//* 函数类型
import type { ApiOption } from "./types";
import disposeParameter from "./disposeParameter";

export const api = {
    server: async (keyword: string, url: string, option?: ApiOption) => {
        //* 统一化参数处理
        const opts = disposeParameter(option);

        //* 服务端渲染请求
        const response = await useAsyncData(keyword, () => $fetch(url, opts));

        //* 服务端渲染请求错误处理
        if (response.error.value) {
            //* 数据初始化
            const error = response.error.value as FetchError;
            const message = error.data.message || error.message || "服务端渲染失败";
            //* 响应错误回调
            if (option?.error) option.error(createError({ statusCode: error.statusCode, message: message }));
            //* 显示错误页面
            throw showError({ statusCode: error.statusCode, message: message });
        } else if (option?.success) {
            option.success(response.data as Ref<any>);
        }

        //* 全局回调
        if (option?.finally) option.finally();

        return response.data as Ref<any>;
    },

    frontend: async (url: string, option?: ApiOption) => {
        //* 统一化参数处理
        const opts = disposeParameter(option);

        //* 自动携带用户指纹
        if (get_canvas_fingerprint) {
            if (opts.headers) (opts.headers as any)["X-Client-Uid"] = get_canvas_fingerprint();
            else {
                opts.headers = {
                    "X-Client-Uid": get_canvas_fingerprint(),
                };
            }
        }

        //* 请求错误处理
        opts.onRequestError = (context) => {
            if (context.options.signal?.aborted) return;
            else {
                //* 消息提示
                $message.error("系统正在维护, 请耐心等待...");

                //* 响应错误回调
                if (option?.error) option.error(createError({ statusCode: 400, message: "系统正在维护, 请耐心等待..." }));

                //* 全局回调
                if (option?.finally) option.finally();
            }
        };

        //* 响应错误处理
        opts.onResponseError = (context) => {
            if (context.options.signal?.aborted) return;
            else {
                //* 数据初始化
                const message = context.response._data.message || context.response.statusText || "未知错误";

                //* 消息提示
                !option?.hidden && $message.error(message);

                //* 响应错误回调
                if (option?.error) option.error(createError({ statusCode: context.response.status || 500, message: message }));

                //* 全局回调
                if (option?.finally) option.finally();
            }
        };

        const response = await $fetch(url, opts);

        //* 响应成功回调
        if (option?.success) option.success(response as any);
        //* 全局回调
        if (option?.finally) option.finally();

        return response as any;
    },

    middleware: async (keyword: string, route: RouteLocationNormalizedGeneric, url: string, option?: ApiOption) => {
        //* 参数初始化
        const opts = option || {};
        //* 语言环境初始化
        opts.headers = Object.assign(option?.headers || {}, { "x-language": (route?.meta?.routeLanguage || i18nOption.locale) as string });
        //* 根据Method进行数据处理
        const method = option?.method?.toLocaleUpperCase() || "GET";
        if (["GET"].includes(method)) opts.query = option?.data;
        else if (["PUT", "POST", "PATCH", "DELETE"].includes(method)) {
            opts.body = option?.data;
        }

        //* 服务端渲染请求
        const response = await useAsyncData(keyword, () => $fetch(url, opts));

        //* 服务端渲染请求错误处理
        if (!response.error.value) option?.success?.(response.data as Ref<any>);
        else {
            //* 响应错误回调
            option?.error?.(
                createError({
                    statusCode: response.error.value.statusCode,
                    message: (response.error.value.data as any)?.message || response.error.value.message || "未知错误",
                })
            );
        }

        //* 全局回调
        if (option?.finally) option.finally();

        return response.data as Ref<any>;
    },
};
